import { generateRoute } from '../../routing/router.js';
import {
    MESSAGE_BLOCKED,
    MESSAGE_LOAD_MORE_FAILED,
    MESSAGE_TEMPORARY_ERROR,
    MESSAGE_MANUAL_GAME_ADDED,
    MESSAGE_MANUAL_GAME_REMOVED,
    MESSAGE_MANUAL_LIST_RESULTS_HINT,
    MESSAGE_MANUAL_SECURITY_TOKEN_MISSING,
    MESSAGE_MANUAL_TOGGLE_FAILED,
    MESSAGE_MIN_QUERY,
    MESSAGE_SEARCHING,
    MIN_QUERY_LENGTH,
    SEARCH_DEBOUNCE_MS,
} from './constants.js';
import { resolveGlobalSearchDom } from './dom.js';
import { resolveCsrfToken } from '../write-request.js';
import {
    formatResultsCountLabel,
    renderAppendedResultCardsMarkup,
    renderResultsMarkup,
    syncLoadMoreButton,
} from './render.js';
import {
    fetchSearchResults,
    toggleManualListMembershipRequest,
} from './search-api.js';
import { createGlobalSearchState } from './state.js';
import { getPagedSearchSurfaceController } from '../paged-search-surface.js';
import { createModalFocusScope } from '../modal-focus-scope.js';

export function initGlobalSearch() {
    const dom = resolveGlobalSearchDom();
    if (dom === null) {
        return;
    }

    const routes = resolveRequiredRoutes();
    if (routes === null) {
        return;
    }

    const defaultTitle = dom.title.textContent?.trim() !== ''
        ? dom.title.textContent
        : 'Global search';
    const state = createGlobalSearchState(defaultTitle);
    const MODAL_OPEN_ANIMATION_MS = 300;
    const MODAL_CLOSE_ANIMATION_MS = 150;
    const RESULTS_EXIT_ANIMATION_MS = 150;
    const SKELETON_EXIT_ANIMATION_MS = 100;
    const RESULTS_ENTRY_ANIMATION_MS = 550;
    const BADGE_FADE_MS = 200;
    const MANUAL_TOGGLE_DEBOUNCE_MS = 300;
    const focusScope = createModalFocusScope({
        dialog: dom.dialog,
        requestClose: () => closeModal(),
    });
    if (focusScope === null) {
        return;
    }

    let modalOpenHandle = null;
    let modalCloseHandle = null;
    let delayedResultsRenderHandle = null;
    let delayedResultsHeightReleaseHandle = null;
    let resultsExitHandle = null;
    // Paging context of the search currently on screen; replaced by every new search.
    let currentPaging = null;
    const setStatus = (message, tone = 'info') => {
        const normalizedMessage = String(message).trim();
        if (normalizedMessage === '') {
            dom.status.textContent = '';
            dom.status.hidden = true;
            delete dom.status.dataset.tone;

            return;
        }

        dom.status.hidden = false;
        dom.status.textContent = normalizedMessage;
        dom.status.dataset.tone = tone;
    };

    const syncClearButton = () => {
        if (!(dom.clearButton instanceof HTMLButtonElement)) {
            return;
        }

        dom.clearButton.hidden = dom.input.value === '';
    };

    const clearStatus = () => {
        setStatus('');
    };

    const clearModalTimers = () => {
        if (modalOpenHandle !== null) {
            window.clearTimeout(modalOpenHandle);
            modalOpenHandle = null;
        }

        if (modalCloseHandle !== null) {
            window.clearTimeout(modalCloseHandle);
            modalCloseHandle = null;
        }
    };

    const clearDelayedResultsRender = () => {
        if (delayedResultsRenderHandle !== null) {
            window.clearTimeout(delayedResultsRenderHandle);
            delayedResultsRenderHandle = null;
        }

        if (delayedResultsHeightReleaseHandle !== null) {
            window.clearTimeout(delayedResultsHeightReleaseHandle);
            delayedResultsHeightReleaseHandle = null;
        }
    };

    const clearResultsExitTimer = () => {
        if (resultsExitHandle === null) {
            return;
        }

        window.clearTimeout(resultsExitHandle);
        resultsExitHandle = null;
    };

    const setInputValidationState = (isInvalid) => {
        if (isInvalid) {
            dom.input.dataset.validationState = 'invalid';
            dom.input.setAttribute('aria-invalid', 'true');

            return;
        }

        delete dom.input.dataset.validationState;
        dom.input.removeAttribute('aria-invalid');
    };

    const syncInputValidationState = (query) => {
        const normalizedQuery = String(query).trim();
        const isValidationError = normalizedQuery.length > 0 && normalizedQuery.length < MIN_QUERY_LENGTH;

        setInputValidationState(isValidationError);

        return isValidationError;
    };

    const resolveRenderOptions = () => ({
        isManualListMode: state.isManualListMode(),
        buildDetailsRoute: (id) => {
            const detailsRoute = generateRoute('app_games_show', { id, from: 'search' });

            return typeof detailsRoute === 'string' ? detailsRoute : null;
        },
        buildAddRoute: (params) => {
            const addRoute = generateRoute('app_games_add', params);

            return typeof addRoute === 'string' ? addRoute : null;
        },
        isTogglePending: (actionKey) => state.isTogglePending(actionKey),
    });

    const renderResults = ({
        query = '',
        items = [],
        searchState = 'ready',
        nextOffset = null,
    } = {}) => {
        clearDelayedResultsRender();

        const buildMarkup = () => renderResultsMarkup({
            query,
            items,
            searchState,
            nextOffset,
        }, resolveRenderOptions());

        const hasSkeletonCards = dom.results.querySelector('.global-search-card-skeleton') !== null;
        const shouldFadeOutSkeletonsBeforeSwap = hasSkeletonCards && searchState !== 'loading';

        if (!shouldFadeOutSkeletonsBeforeSwap) {
            dom.results.style.removeProperty('min-height');
            dom.results.innerHTML = buildMarkup();

            return;
        }

        const currentResultsHeight = Math.ceil(dom.results.getBoundingClientRect().height);
        if (currentResultsHeight > 0) {
            dom.results.style.minHeight = `${currentResultsHeight}px`;
        }

        const skeletonCards = dom.results.querySelectorAll('.global-search-card-skeleton');
        skeletonCards.forEach((card) => {
            if (card instanceof HTMLElement) {
                card.classList.add('global-search-card-skeleton-exit');
            }
        });

        delayedResultsRenderHandle = window.setTimeout(() => {
            delayedResultsRenderHandle = null;
            dom.results.innerHTML = buildMarkup();

            delayedResultsHeightReleaseHandle = window.setTimeout(() => {
                delayedResultsHeightReleaseHandle = null;
                dom.results.style.removeProperty('min-height');
            }, RESULTS_ENTRY_ANIMATION_MS);
        }, SKELETON_EXIT_ANIMATION_MS);
    };

    const renderIdleResults = ({
        animateExit = false,
        searchSequence = null,
    } = {}) => {
        const renderIdleNow = () => {
            if (searchSequence !== null && searchSequence !== state.getSearchSequence()) {
                return;
            }

            renderResults({
                searchState: 'idle',
            });
        };

        clearDelayedResultsRender();

        if (!animateExit) {
            dom.results.style.removeProperty('min-height');
            renderIdleNow();

            return;
        }

        const resultsSection = dom.results.querySelector('.global-search-results-section');
        if (!(resultsSection instanceof HTMLElement)) {
            dom.results.style.removeProperty('min-height');
            renderIdleNow();

            return;
        }

        resultsSection.classList.add('results-exit');
        clearResultsExitTimer();
        resultsExitHandle = window.setTimeout(() => {
            resultsExitHandle = null;
            dom.results.style.removeProperty('min-height');
            renderIdleNow();
        }, RESULTS_EXIT_ANIMATION_MS);
    };

    const applyModeUi = () => {
        const isManualListMode = state.isManualListMode();

        dom.modal.dataset.mode = isManualListMode ? 'manual-list-membership' : 'default';
        dom.title.textContent = isManualListMode ? 'Add game' : state.getDefaultTitle();

        if (!(dom.context instanceof HTMLElement)) {
            return;
        }

        if (!isManualListMode) {
            dom.context.hidden = true;
            dom.context.textContent = '';

            return;
        }

        dom.context.hidden = false;
        dom.context.textContent = MESSAGE_MANUAL_LIST_RESULTS_HINT;
    };

    const resetMode = () => {
        state.resetMode();
        applyModeUi();
    };

    const configureMode = (trigger) => {
        state.configureModeFromTrigger(trigger);
        applyModeUi();
    };

    const clearDebounce = () => {
        const handle = state.getDebounceHandle();
        if (handle === null) {
            return;
        }

        window.clearTimeout(handle);
        state.setDebounceHandle(null);
    };

    const openModal = (trigger = null) => {
        configureMode(trigger);
        clearModalTimers();
        dom.modal.hidden = false;
        dom.modal.dataset.visibilityState = 'opening';
        document.body.classList.add('global-search-open');
        focusScope.activate(trigger);
        modalOpenHandle = window.setTimeout(() => {
            modalOpenHandle = null;

            if (dom.modal.hidden) {
                return;
            }

            dom.modal.dataset.visibilityState = 'open';
            focusScope.focus(dom.input);
        }, MODAL_OPEN_ANIMATION_MS);
    };

    const resetModal = () => {
        clearResultsExitTimer();
        clearDelayedResultsRender();
        dom.results.style.removeProperty('min-height');
        state.nextSearchSequence();
        dom.input.value = '';
        syncClearButton();
        setInputValidationState(false);
        setStatus(MESSAGE_MIN_QUERY, 'info');
        renderIdleResults();
    };

    const closeModal = () => {
        if (dom.modal.hidden || dom.modal.dataset.visibilityState === 'closing') {
            return;
        }

        clearDebounce();
        clearResultsExitTimer();
        clearDelayedResultsRender();
        const shouldReloadAfterClose = state.shouldFallbackReloadAfterManualListChange();

        clearModalTimers();
        dom.modal.dataset.visibilityState = 'closing';
        modalCloseHandle = window.setTimeout(() => {
            modalCloseHandle = null;
            dom.modal.hidden = true;
            delete dom.modal.dataset.visibilityState;
            document.body.classList.remove('global-search-open');
            focusScope.deactivate();
            resetMode();
            resetModal();

            if (shouldReloadAfterClose) {
                window.location.reload();
            }
        }, MODAL_CLOSE_ANIMATION_MS);
    };

    const setToggleLoading = (actionKey, isLoading) => {
        resolveManualActionCards(actionKey).forEach((card) => {
            card.disabled = isLoading;
            if (isLoading) {
                card.dataset.loading = 'true';

                return;
            }

            delete card.dataset.loading;
        });
    };

    const resolveManualActionCards = (actionKey) => {
        const normalizedActionKey = String(actionKey).trim();
        if (normalizedActionKey === '') {
            return [];
        }

        const cards = dom.results.querySelectorAll('[data-global-search-manual-action-key]');

        return Array.from(cards).filter((card) => {
            if (!(card instanceof HTMLButtonElement)) {
                return false;
            }

            return String(card.dataset.globalSearchManualActionKey ?? '').trim() === normalizedActionKey;
        });
    };

    const resolveUniqueManualActionCards = (actionKeys) => {
        const uniqueCards = [];
        const seen = new Set();

        actionKeys.forEach((actionKey) => {
            resolveManualActionCards(actionKey).forEach((card) => {
                if (seen.has(card)) {
                    return;
                }

                seen.add(card);
                uniqueCards.push(card);
            });
        });

        return uniqueCards;
    };

    const setCardLoading = (card, isLoading) => {
        if (!(card instanceof HTMLButtonElement)) {
            return;
        }

        card.disabled = isLoading;
        if (isLoading) {
            card.dataset.loading = 'true';

            return;
        }

        delete card.dataset.loading;
    };

    const fadeInBadge = (badge) => {
        if (!(badge instanceof HTMLElement)) {
            return;
        }

        badge.classList.add('global-search-badge-fade-enter');
        window.requestAnimationFrame(() => {
            badge.classList.remove('global-search-badge-fade-enter');
        });
    };

    const fadeOutBadgeAndRemove = (badge) => {
        if (!(badge instanceof HTMLElement)) {
            return;
        }

        badge.classList.add('global-search-badge-fade-exit');
        window.setTimeout(() => {
            if (badge.isConnected) {
                badge.remove();
            }
        }, BADGE_FADE_MS);
    };

    const updateCardMembershipBadge = (card, isInList) => {
        if (!(card instanceof HTMLElement)) {
            return;
        }

        const badgesContainer = card.querySelector('.global-search-badges');
        if (!(badgesContainer instanceof HTMLElement)) {
            return;
        }

        const existingBadge = badgesContainer.querySelector('.global-search-badge-list-on');

        if (isInList) {
            card.classList.add('global-search-card-on-list');

            if (existingBadge instanceof HTMLElement) {
                existingBadge.classList.remove('global-search-badge-fade-exit');
                existingBadge.textContent = 'On list';

                return;
            }

            const badge = document.createElement('span');
            badge.className = 'global-search-badge global-search-badge-list-on';
            badge.textContent = 'On list';
            badgesContainer.append(badge);
            fadeInBadge(badge);

            return;
        }

        card.classList.remove('global-search-card-on-list');
        if (existingBadge instanceof HTMLElement) {
            fadeOutBadgeAndRemove(existingBadge);
        }
    };

    const updateCardLibraryBadge = (card, inLibrary) => {
        if (!(card instanceof HTMLElement)) {
            return;
        }

        const badgesContainer = card.querySelector('.global-search-badges');
        if (!(badgesContainer instanceof HTMLElement)) {
            return;
        }

        const existingBadge = badgesContainer.querySelector('.global-search-badge-library');

        if (!inLibrary) {
            if (existingBadge instanceof HTMLElement) {
                fadeOutBadgeAndRemove(existingBadge);
            }

            return;
        }

        if (existingBadge instanceof HTMLElement) {
            existingBadge.classList.remove('global-search-badge-fade-exit');
            existingBadge.textContent = 'In library';

            return;
        }

        const badge = document.createElement('span');
        badge.className = 'global-search-badge global-search-badge-library';
        badge.textContent = 'In library';
        badgesContainer.prepend(badge);
        fadeInBadge(badge);
    };

    const promoteCardToLibraryToggle = (card, gameId) => {
        if (!(card instanceof HTMLButtonElement)) {
            return;
        }

        const normalizedGameId = gameId.trim();
        if (normalizedGameId === '') {
            return;
        }

        card.dataset.globalSearchManualActionKey = normalizedGameId;
        card.dataset.globalSearchToggleGameId = normalizedGameId;
        delete card.dataset.globalSearchAddIgdbSourceKey;
        delete card.dataset.globalSearchAddTitle;
        delete card.dataset.globalSearchAddCoverUrl;
    };

    const runSearch = async (query) => {
        const normalizedQuery = String(query).trim();
        const currentSearch = state.nextSearchSequence();
        currentPaging = null;
        const isValidationError = syncInputValidationState(normalizedQuery);
        clearResultsExitTimer();

        if (normalizedQuery.length < MIN_QUERY_LENGTH) {
            setStatus(MESSAGE_MIN_QUERY, isValidationError ? 'error' : 'info');
            renderIdleResults({
                animateExit: true,
                searchSequence: currentSearch,
            });

            return;
        }

        setInputValidationState(false);
        setStatus(MESSAGE_SEARCHING, 'loading');
        renderResults({
            query: normalizedQuery,
            searchState: 'loading',
        });

        const manualListId = state.isManualListMode() ? state.getManualListId() : '';
        const payload = await fetchSearchResults(routes.searchRoute, normalizedQuery, { manualListId });
        if (currentSearch !== state.getSearchSequence()) {
            return;
        }

        if (payload === null) {
            setStatus(MESSAGE_TEMPORARY_ERROR, 'error');
            renderResults({
                query: normalizedQuery,
                searchState: 'error',
            });

            return;
        }

        if (payload.externalUnavailable === true) {
            setStatus(`IGDB is unavailable. ${MESSAGE_BLOCKED}`, 'warning');
            renderResults({
                query: normalizedQuery,
                searchState: 'unavailable',
            });

            return;
        }

        const results = Array.isArray(payload.results) ? payload.results : [];
        const nextOffset = resolveNextOffset(payload);
        currentPaging = {
            searchSequence: currentSearch,
            query: normalizedQuery,
            manualListId,
            items: [...results],
            shownIgdbSourceKeys: new Set(results.map((result) => String(result?.igdbSourceKey ?? '').trim())),
            nextOffset,
            pending: false,
        };

        clearStatus();
        renderResults({
            query: normalizedQuery,
            items: results,
            searchState: 'ready',
            nextOffset,
        });
    };

    const resolveLoadMoreButton = () => {
        const button = dom.results.querySelector('[data-global-search-load-more]');

        return button instanceof HTMLButtonElement ? button : null;
    };

    const isCurrentPaging = (paging) => paging === currentPaging
        && paging.searchSequence === state.getSearchSequence();

    // Cards already on screen may carry membership/library badges set in place after a toggle, so a
    // next page is appended to the grid instead of re-rendering the whole result set.
    const appendNextPage = (paging, newItems) => {
        const grid = dom.results.querySelector('.global-search-results-grid');
        const count = dom.results.querySelector('.global-search-results-count');
        if (!(grid instanceof HTMLElement) || !(count instanceof HTMLElement)) {
            // Nothing was rendered as a card yet, so there is no in-place state to keep.
            renderResults({
                query: paging.query,
                items: paging.items,
                nextOffset: paging.nextOffset,
            });

            return;
        }

        const loadMoreButton = resolveLoadMoreButton();
        const hadFocus = loadMoreButton !== null && document.activeElement === loadMoreButton;
        const firstNewCardIndex = grid.children.length;

        grid.insertAdjacentHTML('beforeend', renderAppendedResultCardsMarkup(newItems, resolveRenderOptions()));
        count.textContent = formatResultsCountLabel(paging.items.length);

        if (paging.nextOffset === null) {
            dom.results.querySelector('[data-global-search-load-more-slot]')?.remove();
            const firstNewCard = grid.children[firstNewCardIndex];
            if (hadFocus && firstNewCard instanceof HTMLElement) {
                firstNewCard.focus();
            }

            return;
        }

        if (loadMoreButton !== null) {
            syncLoadMoreButton(loadMoreButton, false);
        }
    };

    const loadMoreResults = async () => {
        const paging = currentPaging;
        if (paging === null || paging.pending || paging.nextOffset === null || !isCurrentPaging(paging)) {
            return;
        }

        paging.pending = true;
        const loadMoreButton = resolveLoadMoreButton();
        if (loadMoreButton !== null) {
            syncLoadMoreButton(loadMoreButton, true);
        }

        const payload = await fetchSearchResults(routes.searchRoute, paging.query, {
            manualListId: paging.manualListId,
            offset: paging.nextOffset,
        });
        if (!isCurrentPaging(paging)) {
            return;
        }

        paging.pending = false;
        if (payload === null || payload.externalUnavailable === true) {
            setStatus(MESSAGE_LOAD_MORE_FAILED, 'warning');
            const button = resolveLoadMoreButton();
            if (button !== null) {
                syncLoadMoreButton(button, false);
            }

            return;
        }

        if (dom.status.textContent === MESSAGE_LOAD_MORE_FAILED) {
            clearStatus();
        }

        const newItems = (Array.isArray(payload.results) ? payload.results : []).filter((item) => {
            const igdbSourceKey = String(item?.igdbSourceKey ?? '').trim();
            if (igdbSourceKey === '' || paging.shownIgdbSourceKeys.has(igdbSourceKey)) {
                return false;
            }

            paging.shownIgdbSourceKeys.add(igdbSourceKey);

            return true;
        });
        paging.items.push(...newItems);
        paging.nextOffset = resolveNextOffset(payload);

        appendNextPage(paging, newItems);
    };

    /**
     * Hands the reread to the list's paged search surface, which owns it from then on (and rereads what
     * the owner typed last). The surface counts as synced as soon as it has taken the reread, so closing
     * the modal before the answer arrives does not reload the page over it; the full reload on close stays
     * for a page without the surface or a reread that could not start.
     */
    const reloadManualListBrowserSurface = async () => {
        const browserRoot = document.querySelector('[data-lists-manual-browser]');
        const browserController = getPagedSearchSurfaceController(browserRoot);
        if (browserController === undefined || typeof browserController.reload !== 'function') {
            return;
        }

        const reread = browserController.reload('replace');
        state.markManualListSurfaceSynced();

        try {
            await reread;
        } catch (error) {
            state.markManualListChanged();
            throw error;
        }
    };

    const toggleManualListMembership = async ({ actionKey, gameId = '', sourceData = null, card = null }) => {
        if (!state.isManualListMode()) {
            return;
        }

        const toggleUrl = state.getManualListToggleUrl();
        if (toggleUrl === '') {
            return;
        }

        const normalizedActionKey = actionKey.trim();
        const normalizedGameId = gameId.trim();

        const normalizedSourceData = sourceData !== null
            ? {
                igdbSourceKey: String(sourceData.igdbSourceKey ?? '').trim(),
                title: String(sourceData.title ?? '').trim(),
                coverUrl: String(sourceData.coverUrl ?? '').trim(),
            }
            : null;

        const hasValidSourceData = normalizedSourceData !== null
            && normalizedSourceData.igdbSourceKey !== ''
            && normalizedSourceData.title !== ''
            && normalizedSourceData.coverUrl !== '';

        if (
            normalizedActionKey === ''
            || state.isTogglePending(normalizedActionKey)
            || state.isToggleCoolingDown(normalizedActionKey)
        ) {
            return;
        }

        if (normalizedGameId === '' && !hasValidSourceData) {
            return;
        }

        const csrfToken = resolveCsrfToken();
        if (csrfToken === '') {
            setStatus(MESSAGE_MANUAL_SECURITY_TOKEN_MISSING, 'error');

            return;
        }

        state.setToggleCooldown(normalizedActionKey, MANUAL_TOGGLE_DEBOUNCE_MS);
        state.addPendingToggle(normalizedActionKey);
        setToggleLoading(normalizedActionKey, true);
        setCardLoading(card, true);

        try {
            const requestPayload = normalizedGameId !== ''
                ? {
                    game_id: normalizedGameId,
                }
                : {
                    igdb_source_key: normalizedSourceData.igdbSourceKey,
                    title: normalizedSourceData.title,
                    cover_url: normalizedSourceData.coverUrl,
                };

            const result = await toggleManualListMembershipRequest({
                toggleUrl,
                csrfToken,
                payload: requestPayload,
            });

            if (!result.ok) {
                throw new Error(result.errorMessage);
            }

            const payload = (typeof result.payload === 'object' && result.payload !== null)
                ? result.payload
                : {};
            const payloadGameId = typeof payload.gameId === 'string' ? payload.gameId.trim() : '';
            const effectiveGameId = payloadGameId !== '' ? payloadGameId : normalizedGameId;
            const isInList = payload.isInList === true;
            const cardsToUpdate = resolveUniqueManualActionCards([
                normalizedActionKey,
                effectiveGameId,
            ]);

            if (effectiveGameId !== '') {
                state.setToggleCooldown(effectiveGameId, MANUAL_TOGGLE_DEBOUNCE_MS);
            }

            if (isInList) {
                state.markManualListChanged();

                cardsToUpdate.forEach((targetCard) => {
                    updateCardMembershipBadge(targetCard, true);
                    if (normalizedGameId === '' && effectiveGameId !== '') {
                        updateCardLibraryBadge(targetCard, true);
                        promoteCardToLibraryToggle(targetCard, effectiveGameId);
                    }
                });

                setStatus(MESSAGE_MANUAL_GAME_ADDED, 'success');
            } else {
                state.markManualListChanged();

                cardsToUpdate.forEach((targetCard) => {
                    updateCardMembershipBadge(targetCard, false);
                });
                setStatus(MESSAGE_MANUAL_GAME_REMOVED, 'success');
            }

            try {
                await reloadManualListBrowserSurface();
            } catch (_error) {
                // Fall back to a full page reload on modal close if the surface refresh fails.
            }
        } catch (error) {
            const message = error instanceof Error && error.message.trim() !== ''
                ? error.message
                : MESSAGE_MANUAL_TOGGLE_FAILED;
            setStatus(message, 'error');
        } finally {
            state.removePendingToggle(normalizedActionKey);
            setToggleLoading(normalizedActionKey, false);
            setCardLoading(card, false);
        }
    };

    // Delegated, so a trigger rendered later (an empty state swapped in by a list refresh) opens the modal too.
    document.addEventListener('click', (event) => {
        const trigger = typeof event.target?.closest === 'function'
            ? event.target.closest('[data-global-search-trigger]')
            : null;
        if (trigger !== null) {
            openModal(trigger);
        }
    });

    dom.closeButtons.forEach((button) => {
        button.addEventListener('click', closeModal);
    });

    dom.input.addEventListener('input', () => {
        clearDebounce();
        syncInputValidationState(dom.input.value);
        syncClearButton();

        if (dom.input.value.trim() === '') {
            void runSearch(dom.input.value);

            return;
        }

        state.setDebounceHandle(window.setTimeout(() => {
            void runSearch(dom.input.value);
        }, SEARCH_DEBOUNCE_MS));
    });

    if (dom.clearButton instanceof HTMLButtonElement) {
        dom.clearButton.addEventListener('click', () => {
            clearDebounce();
            dom.input.value = '';
            syncClearButton();
            setInputValidationState(false);
            void runSearch('').then(() => {
                dom.input.focus();
            });
        });
    }

    dom.results.addEventListener('click', (event) => {
        const loadMoreButton = event.target instanceof HTMLElement
            ? event.target.closest('[data-global-search-load-more]')
            : null;
        if (loadMoreButton instanceof HTMLButtonElement) {
            event.preventDefault();
            void loadMoreResults();

            return;
        }

        if (!state.isManualListMode()) {
            return;
        }

        const target = event.target instanceof HTMLElement
            ? event.target.closest('[data-global-search-manual-action-key]')
            : null;

        if (!(target instanceof HTMLButtonElement)) {
            return;
        }

        const actionKey = String(target.dataset.globalSearchManualActionKey ?? '').trim();
        if (actionKey === '') {
            return;
        }

        const gameId = String(target.dataset.globalSearchToggleGameId ?? '').trim();
        const sourceData = gameId === ''
            ? {
                igdbSourceKey: String(target.dataset.globalSearchAddIgdbSourceKey ?? '').trim(),
                title: String(target.dataset.globalSearchAddTitle ?? '').trim(),
                coverUrl: String(target.dataset.globalSearchAddCoverUrl ?? '').trim(),
            }
            : null;

        event.preventDefault();
        void toggleManualListMembership({
            actionKey,
            gameId,
            sourceData,
            card: target,
        });
    });

    resetMode();
    resetModal();
    syncClearButton();
}

function resolveNextOffset(payload) {
    return payload !== null && Number.isInteger(payload.nextOffset) ? payload.nextOffset : null;
}

function resolveRequiredRoutes() {
    const searchRoute = generateRoute('app_api_global_game_search');
    const addGameRoute = generateRoute('app_games_add');

    if (typeof searchRoute !== 'string' || typeof addGameRoute !== 'string') {
        return null;
    }

    return {
        searchRoute,
    };
}
